import React, {useEffect, useMemo, useState} from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import {colorPalette, colors} from '../../../core/config/color.config';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'; platform: string}>;
}

const DISMISS_KEY = 'housecall-pwa-install-dismissed-at';
const DISMISS_FOR_MS = 7 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  Boolean((navigator as Navigator & {standalone?: boolean}).standalone);

const isAppleMobileDevice = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const PwaInstallPrompt = () => {
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const appleMobile = useMemo(isAppleMobileDevice, []);

  useEffect(() => {
    if (isStandalone()) {
      return undefined;
    }

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY)) || 0;
    if (Date.now() - dismissedAt < DISMISS_FOR_MS) {
      return undefined;
    }

    const revealTimer = window.setTimeout(() => setVisible(true), 1500);

    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
      setVisible(true);
    };
    const hideAfterInstall = () => {
      setVisible(false);
      setShowInstructions(false);
      setInstallEvent(null);
    };

    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
    window.addEventListener('appinstalled', hideAfterInstall);

    return () => {
      window.clearTimeout(revealTimer);
      window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
      window.removeEventListener('appinstalled', hideAfterInstall);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowInstructions(false);
  };

  const install = async () => {
    if (!installEvent) {
      setShowInstructions(true);
      return;
    }

    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      setVisible(false);
    }
    setInstallEvent(null);
  };

  if (!visible || isStandalone()) {
    return null;
  }

  return (
    <>
      <View style={styles.banner}>
        <View style={styles.bannerCopy}>
          <Text style={styles.title}>Install HouseCall Admin</Text>
          <Text style={styles.description}>
            Add the portal as an app to enable the full-screen experience,
            notifications, and app icon badges.
          </Text>
        </View>
        <TouchableOpacity style={styles.installButton} onPress={install}>
          <Text style={styles.installButtonText}>
            {appleMobile ? 'How to install' : 'Install app'}
          </Text>
        </TouchableOpacity>
        <Pressable
          accessibilityLabel="Dismiss install prompt"
          onPress={dismiss}
          style={styles.dismissButton}>
          <Text style={styles.dismissText}>×</Text>
        </Pressable>
      </View>

      <Modal
        animationType="fade"
        transparent
        visible={showInstructions}
        onRequestClose={() => setShowInstructions(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              Install HouseCall Admin
            </Text>
            {appleMobile ? (
              <>
                <Text style={styles.step}>
                  1. Tap the Share button in Safari or Chrome.
                </Text>
                <Text style={styles.step}>
                  2. Select “Add to Home Screen”.
                </Text>
                <Text style={styles.step}>
                  3. Tap “Add”, then launch HouseCall from its new Home Screen
                  icon.
                </Text>
                <Text style={styles.note}>
                  Open the Home Screen app—not this browser tab—then allow
                  notifications when prompted. Apple requires this manual
                  installation step.
                </Text>
              </>
            ) : (
              <Text style={styles.step}>
                Open your browser menu and choose “Install app” or “Add to Home
                Screen”. If the option is unavailable, use a current browser
                over HTTPS.
              </Text>
            )}
            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => setShowInstructions(false)}>
              <Text style={styles.doneButtonText}>Got it</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  banner: {
    position: 'absolute',
    right: 20,
    bottom: 20,
    left: 20,
    zIndex: 10000,
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 720,
    alignSelf: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: colorPalette.white,
    borderWidth: 1,
    borderColor: colors.gray.lighter,
    shadowColor: colorPalette.black,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.2,
    shadowRadius: 12,
  },
  bannerCopy: {
    flex: 1,
    paddingRight: 12,
  },
  title: {
    color: colorPalette.secondary,
    fontFamily: 'Quicksand-Bold',
    fontSize: 16,
  },
  description: {
    marginTop: 4,
    color: colorPalette.black,
    fontFamily: 'Quicksand-Regular',
    fontSize: 13,
  },
  installButton: {
    paddingHorizontal: 16,
    paddingVertical: 11,
    borderRadius: 8,
    backgroundColor: colorPalette.secondary,
  },
  installButtonText: {
    color: colorPalette.white,
    fontFamily: 'Quicksand-SemiBold',
    fontSize: 13,
  },
  dismissButton: {
    marginLeft: 10,
    padding: 4,
  },
  dismissText: {
    color: colors.gray.darker,
    fontSize: 24,
    lineHeight: 24,
  },
  modalBackdrop: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  modalCard: {
    width: '100%',
    maxWidth: 480,
    padding: 24,
    borderRadius: 14,
    backgroundColor: colorPalette.white,
  },
  modalTitle: {
    marginBottom: 16,
    color: colorPalette.secondary,
    fontFamily: 'Quicksand-Bold',
    fontSize: 21,
  },
  step: {
    marginBottom: 12,
    color: colorPalette.black,
    fontFamily: 'Quicksand-Medium',
    fontSize: 15,
    lineHeight: 22,
  },
  note: {
    marginTop: 6,
    padding: 12,
    borderRadius: 8,
    color: colorPalette.black,
    backgroundColor: colors.gray.lighter,
    fontFamily: 'Quicksand-Regular',
    fontSize: 13,
    lineHeight: 19,
  },
  doneButton: {
    alignSelf: 'flex-end',
    marginTop: 20,
    paddingHorizontal: 20,
    paddingVertical: 11,
    borderRadius: 8,
    backgroundColor: colorPalette.secondary,
  },
  doneButtonText: {
    color: colorPalette.white,
    fontFamily: 'Quicksand-SemiBold',
  },
});

export default PwaInstallPrompt;
